/**
 * Security Utilities
 * Input sanitization, password validation, and XSS prevention
 */

// ============= PASSWORD VALIDATION =============

export interface PasswordStrength {
  score: number; // 0-4 (weak to strong)
  feedback: string[];
  isValid: boolean;
  meetsAllRequirements: boolean;
}

// Password requirements - relaxed for user convenience
const MIN_PASSWORD_LENGTH = 8;
const STRONG_PASSWORD_LENGTH = 12;
const ADMIN_MIN_PASSWORD_LENGTH = 16;

export interface PasswordRequirements {
  minLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
}

export const STANDARD_PASSWORD_REQUIREMENTS: PasswordRequirements = {
  minLength: 8,
  requireLowercase: false,
  requireUppercase: false,
  requireNumbers: false,
  requireSpecialChars: false,
};

export const ADMIN_PASSWORD_REQUIREMENTS: PasswordRequirements = {
  minLength: 16,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

/**
 * Validates password strength with comprehensive checks
 * Hardened for security: requires 12+ chars, mixed case, numbers, and symbols
 */
export function validatePasswordStrength(
  password: string,
  requirements: PasswordRequirements = STANDARD_PASSWORD_REQUIREMENTS
): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;
  let meetsAllRequirements = true;

  // Length checks
  if (password.length < requirements.minLength) {
    feedback.push(`Password must be at least ${requirements.minLength} characters`);
    meetsAllRequirements = false;
  } else {
    score += 2; // Base score for meeting minimum length
  }

  if (password.length >= STRONG_PASSWORD_LENGTH) {
    score += 1;
  }

  // Optional complexity bonuses (not required, but improve score)
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password);

  // Only enforce requirements if they are set
  if (requirements.requireLowercase) {
    if (hasLowercase) {
      score += 0.25;
    } else {
      feedback.push('Must include lowercase letters (a-z)');
      meetsAllRequirements = false;
    }
  } else if (hasLowercase) {
    score += 0.25; // Bonus for having it even if not required
  }

  if (requirements.requireUppercase) {
    if (hasUppercase) {
      score += 0.25;
    } else {
      feedback.push('Must include uppercase letters (A-Z)');
      meetsAllRequirements = false;
    }
  } else if (hasUppercase) {
    score += 0.25;
  }

  if (requirements.requireNumbers) {
    if (hasNumbers) {
      score += 0.25;
    } else {
      feedback.push('Must include numbers (0-9)');
      meetsAllRequirements = false;
    }
  } else if (hasNumbers) {
    score += 0.25;
  }

  if (requirements.requireSpecialChars) {
    if (hasSpecialChars) {
      score += 0.25;
    } else {
      feedback.push('Must include special characters (!@#$%^&*...)');
      meetsAllRequirements = false;
    }
  } else if (hasSpecialChars) {
    score += 0.25;
  }

  // Common patterns to avoid (expanded list)
  const commonPatterns = [
    /^123456/,
    /^password/i,
    /^qwerty/i,
    /^abc123/i,
    /(.)\1{2,}/, // 3+ repeated characters
    /^letmein/i,
    /^welcome/i,
    /^admin/i,
    /^login/i,
    /^passw0rd/i,
    /^12345678/,
    /^1234567890/,
    /^iloveyou/i,
    /^sunshine/i,
    /^princess/i,
    /^football/i,
    /^monkey/i,
    /^shadow/i,
    /^master/i,
    /^dragon/i,
    /^111111/,
    /^000000/,
  ];

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      score = Math.max(0, score - 1);
      feedback.push('Avoid common password patterns');
      meetsAllRequirements = false;
      break;
    }
  }

  // Check for sequential characters
  if (/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(password) ||
      /(?:012|123|234|345|456|567|678|789)/.test(password)) {
    score = Math.max(0, score - 0.5);
    feedback.push('Avoid sequential characters');
  }

  const finalScore = Math.min(4, Math.floor(score));

  return {
    score: finalScore,
    feedback: feedback.slice(0, 4), // Limit to 4 suggestions
    isValid: password.length >= requirements.minLength,
    meetsAllRequirements,
  };
}

/**
 * Get password strength label
 */
export function getPasswordStrengthLabel(score: number): {
  label: string;
  color: string;
} {
  switch (score) {
    case 0:
      return { label: 'Very Weak', color: 'text-destructive' };
    case 1:
      return { label: 'Weak', color: 'text-destructive' };
    case 2:
      return { label: 'Fair', color: 'text-yellow-500' };
    case 3:
      return { label: 'Good', color: 'text-green-500' };
    case 4:
      return { label: 'Strong', color: 'text-green-600' };
    default:
      return { label: 'Unknown', color: 'text-muted-foreground' };
  }
}

// ============= INPUT SANITIZATION =============

/**
 * Sanitizes text input to prevent XSS attacks
 * Escapes HTML entities and removes potentially dangerous content
 */
export function sanitizeTextInput(input: string): string {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');

  // Escape HTML entities
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

  // Trim and normalize whitespace
  sanitized = sanitized.trim().replace(/\s+/g, ' ');

  return sanitized;
}

/**
 * Sanitizes text for display (allows safe HTML but escapes dangerous content)
 */
export function sanitizeForDisplay(input: string): string {
  if (typeof input !== 'string') {
    return '';
  }

  // Remove script tags and event handlers
  let sanitized = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '');

  return sanitized;
}

/**
 * Validates and sanitizes email input
 */
export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') {
    return '';
  }

  // Basic sanitization
  let sanitized = email.trim().toLowerCase();

  // Remove any HTML or script content
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  return sanitized;
}

/**
 * Validates email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// ============= FEEDBACK / MESSAGE VALIDATION =============

const MAX_FEEDBACK_LENGTH = 5000;
const MAX_DISPLAY_NAME_LENGTH = 100;

/**
 * Validates feedback message
 */
export function validateFeedbackMessage(message: string): {
  isValid: boolean;
  sanitized: string;
  error?: string;
} {
  if (!message || typeof message !== 'string') {
    return { isValid: false, sanitized: '', error: 'Message is required' };
  }

  const trimmed = message.trim();

  if (trimmed.length === 0) {
    return { isValid: false, sanitized: '', error: 'Message cannot be empty' };
  }

  if (trimmed.length > MAX_FEEDBACK_LENGTH) {
    return {
      isValid: false,
      sanitized: '',
      error: `Message must be less than ${MAX_FEEDBACK_LENGTH} characters`,
    };
  }

  // Sanitize for storage (basic XSS prevention)
  const sanitized = sanitizeTextInput(trimmed);

  return { isValid: true, sanitized };
}

/**
 * Validates display name
 */
export function validateDisplayName(name: string): {
  isValid: boolean;
  sanitized: string;
  error?: string;
} {
  if (!name || typeof name !== 'string') {
    return { isValid: true, sanitized: '' }; // Optional field
  }

  const trimmed = name.trim();

  if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
    return {
      isValid: false,
      sanitized: '',
      error: `Name must be less than ${MAX_DISPLAY_NAME_LENGTH} characters`,
    };
  }

  // Remove HTML tags but allow basic characters
  const sanitized = trimmed.replace(/<[^>]*>/g, '');

  return { isValid: true, sanitized };
}

// ============= ADMIN ACTIVITY LOGGING =============

export type AdminActionType =
  | 'admin_add'
  | 'admin_subtract'
  | 'settings_update'
  | 'sync_trigger'
  | 'deposit_status_change'
  | 'admin_create'
  | 'admin_revoke'
  | 'page_update'
  | 'notification_send'
  | 'user_delete'
  | 'partner_create'
  | 'partner_update'
  | 'partner_delete'
  | 'partner_visibility_change';

export interface AdminActivityLog {
  action_type: AdminActionType;
  target_type?: 'user' | 'match' | 'prediction' | 'setting' | 'sync' | 'deposit' | 'page' | 'notification' | 'affiliate_brand';
  target_id?: string;
  old_value?: Record<string, unknown>;
  new_value?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

// ============= DEVICE FINGERPRINTING (Basic) =============

/**
 * Generates a simple browser fingerprint for abuse detection
 * This is NOT meant to be cryptographically secure, just a basic heuristic
 */
export function generateBrowserFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
  ];

  // Simple hash
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return Math.abs(hash).toString(36);
}

// ============= URL VALIDATION =============

/**
 * Validates and sanitizes URL for safe usage
 */
export function sanitizeUrl(url: string): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    const parsed = new URL(url);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}
